import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { makeMatcher } from '../../services/search';
import SearchField from '../SearchField';

const emptyForm = { name: '', description: '', icon: '', color: '#0F56E3', order: 0, isActive: true };

export default function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');

  // Accolades obligatoires : si load retournait la Promise, useEffect(load, [])
  // la stockerait comme fonction de nettoyage -> au démontage (navigation vers
  // une autre page) React l'appelle -> "destroy is not a function" -> page blanche.
  const load = () => {
    api.get('/groups')
      .then(r => setGroups(r.data.data || []))
      .catch(e => setMsg(`Erreur : ${e.response?.data?.message || e.message}`))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Filtrage local : la liste tient en mémoire, inutile de repasser par l'API.
  const correspond = makeMatcher(search);
  const groupesFiltres = groups.filter(g => correspond(g.name, g.description));

  const openCreate = () => { setForm(emptyForm); setEditGroup(null); setShowModal(true); };
  const openEdit = (g) => { setForm(g); setEditGroup(g); setShowModal(true); };

  const handleSave = async () => {
    try {
      if (editGroup) await api.put(`/groups/${editGroup.id}`, form);
      else await api.post('/groups', form);
      setShowModal(false); setMsg('Sauvegardé !'); load();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) { setMsg(`Erreur : ${e.response?.data?.message || e.message}`); }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Supprimer le groupe "${group.name}" ? Les outils et utilisateurs ne sont pas supprimés, seul le rattachement disparaît.`)) return;
    try {
      await api.delete(`/groups/${group.id}`);
      load();
    } catch (e) { setMsg(`Erreur : ${e.response?.data?.message || e.message}`); }
  };

  return (
    <div>
      <div style={styles.header}>
        <h2 style={styles.title}>Groupes</h2>
        <SearchField
          className="admin-search"
          value={search}
          onChange={setSearch}
          placeholder="Rechercher un groupe..."
        />
        <button onClick={openCreate} style={styles.addBtn}>+ Nouveau groupe</button>
      </div>
      {msg && <div style={{ ...styles.msg, background: msg.startsWith('Erreur') ? '#fff0ed' : '#e8f8e8', color: msg.startsWith('Erreur') ? '#E46138' : '#2d6a2d' }}>{msg}</div>}

      {loading ? <p>Chargement...</p> : groupesFiltres.length === 0 ? (
        <div style={styles.emptyRow}>
          {search.trim()
            ? `Aucun groupe ne correspond à « ${search.trim()} ».`
            : 'Aucun groupe enregistré pour le moment.'}
        </div>
      ) : (
        <div style={styles.grid}>
          {groupesFiltres.map(g => (
            <div key={g.id} style={styles.card}>
              <div style={{ ...styles.iconBox, background: g.color || '#0F56E3' }}>
                <span style={{ fontSize: 28 }}>{g.icon || '📁'}</span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{g.name}</div>
                <div style={{ fontSize: 13, color: '#7F7F7F' }}>{g.description || 'Aucune description'}</div>
                <div style={{ fontSize: 12, color: '#019EF7', marginTop: 4 }}>
                  {g.applications?.length || 0} outil(s) · {g.users?.length || 0} utilisateur(s)
                </div>
              </div>
              <div style={styles.actions}>
                <button onClick={() => openEdit(g)} style={styles.editBtn}>✏</button>
                <button onClick={() => handleDelete(g)} style={styles.deleteBtn}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h3>{editGroup ? 'Modifier le groupe' : 'Nouveau groupe'}</h3>
              <button onClick={() => setShowModal(false)} style={styles.closeBtn}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.field}>
                <label style={styles.label}>Nom *</label>
                <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} style={styles.input} required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Description</label>
                <input value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} style={styles.input} />
              </div>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Icône (emoji)</label>
                  <input value={form.icon || ''} onChange={e => setForm({...form, icon: e.target.value})} placeholder="⚡" style={styles.input} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Couleur</label>
                  <input type="color" value={form.color || '#0F56E3'} onChange={e => setForm({...form, color: e.target.value})} style={{ ...styles.input, height: 40, padding: 4 }} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Ordre</label>
                  <input type="number" value={form.order || 0} onChange={e => setForm({...form, order: parseInt(e.target.value) || 0})} style={styles.input} />
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setShowModal(false)} style={styles.cancelBtn}>Annuler</button>
              <button onClick={handleSave} style={styles.saveBtn}>Sauvegarder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#062259' },
  addBtn: { marginLeft: 'auto', flexShrink: 0, background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
  emptyRow: { padding: '28px 20px', textAlign: 'center', fontSize: 14, color: '#5f6673' },
  msg: { padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 },
  card: { background: '#fff', borderRadius: 12, padding: 20, display: 'flex', alignItems: 'center', gap: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  iconBox: { width: 52, height: 52, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  actions: { display: 'flex', gap: 6 },
  editBtn: { background: '#EEF3FF', color: '#0F56E3', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer' },
  deleteBtn: { background: '#fff0ed', color: '#E46138', border: 'none', borderRadius: 6, width: 32, height: 32, cursor: 'pointer' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal: { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #f4f6f9' },
  closeBtn: { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#7F7F7F' },
  modalBody: { padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '16px 24px', borderTop: '1px solid #f4f6f9' },
  field: { display: 'flex', flexDirection: 'column', gap: 6, flex: 1 },
  label: { fontSize: 13, fontWeight: 500, color: '#1a1a2e' },
  input: { padding: '10px 12px', borderRadius: 8, border: '1.5px solid #D9D9D9', fontSize: 14, color: '#1a1a2e', width: '100%' },
  row: { display: 'flex', gap: 12 },
  cancelBtn: { background: '#f4f6f9', color: '#7F7F7F', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, cursor: 'pointer' },
  saveBtn: { background: '#062259', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
